import { useState } from "react";
import type { ReactNode } from "react";
import { Button } from "../../../components/ui/Button";
import { Menu } from "../../../components/ui/Menu";
import { useModalFocus } from "../../../hooks/useModalFocus";
import { useT } from "../../../i18n";
import { courseTone } from "../api/consoleApi";
import { TIMER_CHOICES, fmtLeft, needsLook, sortStudents } from "../lib/consoleView";
import type {
  ConsoleCmd,
  ConsoleCourse,
  ConsoleMode,
  ConsoleRoom,
  ConsoleStudent,
  TeacherConsole,
} from "../types";

/** 연결 상태 점(설명서 4.2 — 초록 정상 · 빨강 끊김 · 회색 작품 없음 · 노랑 분리) */
const DOT: Record<ConsoleStudent["connection"], string> = {
  online: "ok",
  dropped: "err",
  isolated: "warn",
  offline: "off",
};

/**
 * FS-11 · 2 진행 — 맨 위 줄(설명서 2.4 ①~⑤): 시계 · 룸 상태 · ▶ 시작 · ⏸ 일시정지 · ⏹ 판 종료(2026-10-11 · 룸은 그대로 · 처음 상태로 멈춤 → ▶ 시작 = 새 판) · ⟲ 모두 제자리 · 모드 ▾ · 타이머 ▾ · (모드에 따라 도구) · ■ 룸 종료(확인 한 번 더).
 * 시작 · 일시정지 · 모두 제자리 · 종료 = 1클릭, 모드 · 타이머 = 펼쳐 2클릭(FF-TCH-001).
 */
export function RoomControl({
  room,
  modes,
  busy,
  onCmd,
  onMode,
  onEnd,
  tools,
}: {
  room: ConsoleRoom;
  modes: ConsoleMode[];
  busy: string | null;
  onCmd: (key: string, c: ConsoleCmd) => void;
  onMode: (id: string) => void;
  onEnd: () => void;
  /** 모드에 따라 붙는 보조 버튼(🧸 배치 · 🔗 구간 진행 — 보완 v0.1 2.1 · 2.2) */
  tools?: ReactNode;
}) {
  const t = useT();
  const [confirming, setConfirming] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const prewarm = room.state === "prewarm";
  const cur = modes.find((m) => m.id === room.modeId);
  const rec = (m: ConsoleMode | undefined) =>
    m?.recommended ? (
      <span className="pf-chip-static pf-chip-static--ok">{t("teacher.room.recommended")}</span>
    ) : null;
  const timerLabel = room.timer
    ? picked
      ? t("teacher.room.timerMin", { n: picked / 60 })
      : fmtLeft(room.timer.endsInS)
    : t("teacher.room.timerOff");

  return (
    <section
      className="pf-pane pf-pane--hero pf-prog"
      aria-labelledby="rc-title"
      data-testid="fs-11-01"
    >
      <h2 id="rc-title" className="pf-sr-only">
        {t("teacher.room.control")}
      </h2>
      <div className="pf-prog__bar">
        <p
          className="pf-prog__clock"
          data-testid="rc-clock"
          aria-label={t("teacher.room.clockLabel")}
        >
          {room.timer ? fmtLeft(room.timer.endsInS) : t("teacher.room.timerOff")}
        </p>
        <span
          className={`pf-chip-static pf-prog__state pf-chip-static--${room.state === "open" ? "ok" : room.state === "paused" ? "warn" : "info"}`}
          data-testid="rc-state"
          data-state={room.state}
        >
          {t(`teacher.room.state.${room.state}`)}
        </span>
        <div className="pf-rc__controls">
          <Button
            variant="green"
            className="pf-btn--sm"
            disabled={room.state !== "paused" || busy !== null}
            data-testid="rc-start"
            onClick={() => onCmd("resume", { kind: "resume" })}
          >
            {t("teacher.room.start")}
          </Button>
          <Button
            variant="accent"
            className="pf-btn--sm"
            disabled={room.state !== "open" || busy !== null}
            data-testid="rc-pause"
            onClick={() => onCmd("pause", { kind: "pause" })}
          >
            {t("teacher.room.pause")}
          </Button>
          <Button
            variant="ghost"
            className="pf-btn--sm"
            disabled={prewarm || busy !== null}
            data-testid="rc-stop"
            title={t("teacher.room.stopHint")}
            onClick={() => onCmd("stop", { kind: "stop" })}
          >
            {t("teacher.room.stop")}
          </Button>
          <Button
            variant="ghost"
            className="pf-btn--sm"
            disabled={prewarm || busy !== null}
            data-testid="rc-reset"
            onClick={() => onCmd("reset", { kind: "reset" })}
          >
            {t("teacher.room.reset")}
          </Button>
          <span className="pf-rc__label" aria-hidden="true">
            {t("teacher.room.modeLabel")}
          </span>
          <Menu
            testId="rc-mode"
            ariaLabel={t("teacher.room.modeLabel")}
            disabled={prewarm || busy !== null}
            label={
              <>
                {t(`teacher.room.mode.${room.modeId}`)} ▾ {rec(cur)}
              </>
            }
            items={modes.map((m) => ({
              id: m.id,
              on: m.id === room.modeId,
              label: (
                <>
                  {t(`teacher.room.mode.${m.id}`)} {rec(m)}
                </>
              ),
            }))}
            onPick={onMode}
          />
          <span className="pf-rc__label" aria-hidden="true">
            {t("teacher.room.timerLabel")}
          </span>
          <Menu
            testId="rc-timer"
            ariaLabel={t("teacher.room.timerLabel")}
            disabled={prewarm || busy !== null}
            label={<>{timerLabel} ▾</>}
            items={TIMER_CHOICES.map((s) => ({
              id: String(s ?? "off"),
              on: s === null ? !room.timer : !!room.timer && picked === s,
              label:
                s === null ? t("teacher.room.timerOff") : t("teacher.room.timerMin", { n: s / 60 }),
            }))}
            onPick={(id) => {
              const s = id === "off" ? null : Number(id);
              setPicked(s);
              onCmd("timer", { kind: "timer", seconds: s });
            }}
          />
          {tools}
        </div>
        <EndButton
          busy={busy}
          onEnd={onEnd}
          confirming={confirming}
          setConfirming={setConfirming}
        />
      </div>
    </section>
  );
}

/** ■ 룸 종료 — 오른쪽 끝 · 확인 창을 한 번 더(진행 · 마무리 같은 버튼) */
export function EndButton({
  busy,
  onEnd,
  confirming,
  setConfirming,
  testId = "rc-end",
}: {
  busy: string | null;
  onEnd: () => void;
  confirming: boolean;
  setConfirming: (v: boolean) => void;
  testId?: string;
}) {
  const t = useT();
  return (
    <>
      <Button
        variant="ghost"
        className="pf-btn--sm pf-rc__end"
        disabled={busy !== null}
        data-testid={testId}
        onClick={() => setConfirming(true)}
      >
        {t("teacher.room.end")}
      </Button>
      {confirming && (
        <ConfirmEnd
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            onEnd();
          }}
        />
      )}
    </>
  );
}

function ConfirmEnd({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  const t = useT();
  const cancel = useModalFocus<HTMLButtonElement>();
  return (
    <div
      className="pf-modal"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <div
        className="pf-pane pf-pane--hero pf-modal__pane pf-rc__confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="rc-confirm-title"
        aria-describedby="rc-confirm-text"
        data-testid="rc-confirm"
      >
        <h2 id="rc-confirm-title" className="pf-h">
          {t("teacher.room.confirmTitle")}
        </h2>
        <p id="rc-confirm-text" className="pf-muted">
          {t("teacher.room.confirmText")}
        </p>
        <div className="pf-row">
          <Button data-testid="rc-confirm-end" onClick={onConfirm}>
            {t("teacher.room.confirmEnd")}
          </Button>
          <Button ref={cancel} variant="ghost" onClick={onCancel}>
            {t("teacher.room.cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * FS-11 · 2 진행 — 학생(설명서 2.4 ⑥~⑩).
 *  - 살펴볼 학생 띠: 끊김 · 분리 · 막힌 학생만 맨 위 칩(누르면 그 학생을 고른다). 없으면 띠 자리에 한 줄 안내.
 *  - 학생 타일: 모두 같은 크기 · 이름순 고정(점수 · 순위 정렬 없음) · 이름 · 기계 · 지금 하는 일 · 상태 점.
 *  - 도와주기: 고른 학생 한 명에게만 4버튼 — 기계 제자리로 · 구경만 하게(분리) · ⭐ 칠판에 크게 · 💡 원리 보기.
 *    켤 수 있는 범위는 예전 표의 개별 조치와 같다(기계 있음 · 미접속 아님 · 예열 아님 · 교류 룸이면 우리 반 학생만 — Q-5).
 *  - 교류 룸이면 반 필터 칩 · 타일 반 색 테두리 · 옆 반 학생은 도와주기 대신 이유 한 줄.
 */
export function StudentTiles({
  room,
  students,
  busy,
  onCmd,
  roster,
  courses,
  principle,
  onPrinciple,
}: {
  room: ConsoleRoom;
  students: ConsoleStudent[];
  busy: string | null;
  onCmd: (key: string, c: ConsoleCmd) => void;
  roster: TeacherConsole["roster"];
  /** 교류 룸 코스(반 룸이면 빈 목록) */
  courses: readonly ConsoleCourse[];
  /** 💡 원리 보기로 고른 기계(null = 닫힘) */
  principle: number | null;
  onPrinciple: (machine: number) => void;
}) {
  const t = useT();
  const exchange = courses.length > 1;
  const [only, setOnly] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const label = new Map(courses.map((c) => [c.courseId, c.label]));
  const shown = exchange && only ? students.filter((s) => s.courseId === only) : students;
  const rows = sortStudents(shown, "name");
  const look = needsLook(shown);
  const sel = students.find((s) => s.pid === picked) ?? null;

  const conn = (s: ConsoleStudent) =>
    s.connection === "dropped"
      ? s.droppedS != null
        ? t("teacher.room.conn.dropped", { s: s.droppedS })
        : t("teacher.room.conn.droppedNoS")
      : t(`teacher.room.conn.${s.connection}`);
  const now = (s: ConsoleStudent) => {
    const a = s.activity;
    if (s.connection === "isolated") return t("teacher.room.act.isolated");
    if (s.connection === "dropped") return t("teacher.room.act.reconnecting");
    if (!a || s.connection === "offline") return "—";
    if (a.kind === "match")
      return a.paused
        ? t("teacher.room.act.matchPaused")
        : t("teacher.room.act.match", { vs: a.vs });
    // 팀 번호는 서버(room-sim)가 0 부터 준다 — 화면만 1 부터(편성 '팀 n' 슬롯 · 릴레이 구간과 같게)
    if (a.kind === "team") return t("teacher.room.act.team", { n: a.team + 1 });
    if (a.kind === "relay")
      return t("teacher.room.act.relay", {
        n: a.stage + 1,
        status: t(`relay.status.${a.status}`),
      });
    return t(`teacher.room.act.${a.kind}`);
  };
  const machineOf = (s: ConsoleStudent) =>
    s.work ? t("teacher.room.work", { v: s.work.version, title: s.work.title }).trim() : "—";
  const pick = (pid: string) => setPicked((cur) => (cur === pid ? null : pid));

  return (
    <section
      className="pf-pane pf-rc__students"
      aria-labelledby="rc-students"
      data-testid="fs-11-02"
    >
      <div className="pf-row">
        <h2 id="rc-students" className="pf-h3">
          {t("teacher.room.students")}
        </h2>
        {roster && (
          <span className="pf-muted pf-row__end" data-testid="rc-roster">
            {t("teacher.room.roster", {
              n: roster.count,
              source: t(`teacher.room.rosterSource.${roster.source}`),
            })}
          </span>
        )}
      </div>
      <div className="pf-look" data-testid="rc-attention">
        <span className="pf-look__label">{t("teacher.room.attention")}</span>
        {look.length === 0 ? (
          <span className="pf-muted">{t("teacher.room.attentionNone")}</span>
        ) : (
          look.map((s) => (
            <button
              key={s.pid}
              type="button"
              className={`pf-chip pf-look__chip pf-look__chip--${s.connection === "dropped" ? "err" : s.connection === "isolated" ? "warn" : "err"}${picked === s.pid ? " pf-chip--on" : ""}`}
              aria-pressed={picked === s.pid}
              data-pid={s.pid}
              onClick={() => pick(s.pid)}
            >
              {s.nickname} ·{" "}
              {s.connection === "online" && s.stalled.length
                ? s.stalled.map((p) => t("teacher.room.stalled", { port: p })).join(" ")
                : conn(s)}
            </button>
          ))
        )}
      </div>
      {exchange && (
        <div
          className="pf-row pf-row--wrap pf-rc__filter"
          role="group"
          aria-label={t("teacher.room.exchange.filter")}
          data-testid="rc-course-filter"
        >
          {[null, ...courses.map((c) => c.courseId)].map((id) => {
            const c = courses.find((x) => x.courseId === id);
            return (
              <button
                key={id ?? "all"}
                type="button"
                className={`pf-chip${only === id ? " pf-chip--on" : ""}`}
                aria-pressed={only === id}
                data-course={id ?? "all"}
                data-tone={courseTone(courses, id)}
                onClick={() => setOnly(id)}
              >
                {c
                  ? t(
                      c.mine ? "teacher.room.exchange.chipMine" : "teacher.room.exchange.chipOther",
                      { label: c.label },
                    )
                  : t("teacher.room.exchange.chipAll")}
              </button>
            );
          })}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="pf-muted">{t("teacher.room.noStudents")}</p>
      ) : (
        <ul className="pf-kids" data-testid="rc-tiles" aria-labelledby="rc-students">
          {rows.map((s) => (
            <li key={s.pid}>
              <button
                type="button"
                className={`pf-kid${picked === s.pid ? " pf-kid--on" : ""}`}
                aria-pressed={picked === s.pid}
                data-pid={s.pid}
                data-conn={s.connection}
                data-course={exchange ? (s.courseId ?? undefined) : undefined}
                data-mine={exchange ? String(s.mine) : undefined}
                data-tone={exchange ? courseTone(courses, s.courseId) : undefined}
                data-lit={s.machine !== null && room.highlight === s.machine ? "true" : undefined}
                onClick={() => pick(s.pid)}
              >
                <span className="pf-kid__head">
                  <i
                    className={`pf-kid__dot pf-kid__dot--${s.work ? DOT[s.connection] : "off"}`}
                    aria-hidden="true"
                  />
                  <b className="pf-kid__name">{s.nickname}</b>
                  {s.machine !== null && room.highlight === s.machine && (
                    <span aria-label={t("teacher.room.litNow")}>⭐</span>
                  )}
                </span>
                <span className="pf-kid__machine">{machineOf(s)}</span>
                <span className="pf-kid__now">{now(s)}</span>
                <span className="pf-kid__conn">{conn(s)}</span>
                {exchange && (
                  <span
                    className="pf-chip-static pf-rc__course"
                    data-tone={courseTone(courses, s.courseId)}
                  >
                    {label.get(s.courseId ?? "") ?? "—"}
                  </span>
                )}
                {s.stalled.map((p) => (
                  <span key={p} className="pf-chip-static pf-chip-static--err" data-stalled={p}>
                    {t("teacher.room.stalled", { port: p })}
                  </span>
                ))}
              </button>
            </li>
          ))}
        </ul>
      )}
      {sel ? (
        <HelpPanel
          s={sel}
          room={room}
          exchange={exchange}
          courseLabel={label.get(sel.courseId ?? "") ?? ""}
          busy={busy}
          onCmd={onCmd}
          principle={principle}
          onPrinciple={onPrinciple}
          onClose={() => setPicked(null)}
        />
      ) : null}
      <p className="pf-muted" data-testid="rc-sort-note">
        {t("teacher.room.sortNote")}
      </p>
      {exchange && (
        <p className="pf-muted" data-testid="rc-scope-note">
          {t("teacher.room.exchange.scopeNote")}
        </p>
      )}
    </section>
  );
}

/** 도와주기(설명서 2.4 ⑨) — 고른 학생 한 명에게만 4버튼 */
function HelpPanel({
  s,
  room,
  exchange,
  courseLabel,
  busy,
  onCmd,
  principle,
  onPrinciple,
  onClose,
}: {
  s: ConsoleStudent;
  room: ConsoleRoom;
  exchange: boolean;
  courseLabel: string;
  busy: string | null;
  onCmd: (key: string, c: ConsoleCmd) => void;
  principle: number | null;
  onPrinciple: (machine: number) => void;
  onClose: () => void;
}) {
  const t = useT();
  // Q-5 — 옆 반 학생의 개별 조치(제자리 · 구경 · ⭐)는 그 반 선생님이(서버도 403)
  const other = exchange && !s.mine;
  const can =
    !other && s.machine !== null && s.connection !== "offline" && room.state !== "prewarm";
  const iso = s.connection === "isolated";
  const lit = s.machine !== null && room.highlight === s.machine;
  const shown = s.machine !== null && principle === s.machine;
  return (
    <div
      className="pf-help"
      role="group"
      aria-labelledby="rc-help-title"
      data-testid="rc-help"
      data-pid={s.pid}
    >
      <h3 id="rc-help-title" className="pf-help__title">
        {t("teacher.room.helpTitle", { name: s.nickname })}
      </h3>
      <div className="pf-row pf-row--wrap">
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={!can || busy !== null}
          aria-label={t("teacher.room.resetOf", { name: s.nickname })}
          data-act="resetMachine"
          onClick={() => onCmd(`resetMachine:${s.pid}`, { kind: "resetMachine", pid: s.pid })}
        >
          {t("teacher.room.resetMachine")}
        </Button>
        <Button
          variant="ghost"
          className="pf-btn--sm"
          disabled={!can || busy !== null}
          aria-label={t(iso ? "teacher.room.restoreOf" : "teacher.room.isolateOf", {
            name: s.nickname,
          })}
          data-act={iso ? "restore" : "isolate"}
          onClick={() =>
            onCmd(`${iso ? "restore" : "isolate"}:${s.pid}`, {
              kind: iso ? "restore" : "isolate",
              pid: s.pid,
            })
          }
        >
          {t(iso ? "teacher.room.restore" : "teacher.room.isolate")}
        </Button>
        <Button
          variant={lit ? "accent" : "ghost"}
          className="pf-btn--sm"
          disabled={!can || busy !== null}
          aria-pressed={lit}
          aria-label={t("teacher.room.highlightOf", { name: s.nickname })}
          title={t("teacher.room.highlightHint")}
          data-act="highlight"
          onClick={() =>
            onCmd(`highlight:${s.pid}`, { kind: "highlight", pid: lit ? null : s.pid })
          }
        >
          {t("teacher.room.highlight")}
        </Button>
        <Button
          variant={shown ? "accent" : "ghost"}
          className="pf-btn--sm"
          disabled={!can}
          aria-pressed={shown}
          aria-label={t("teacher.room.principleOf", { name: s.nickname })}
          title={t("teacher.room.principleHint")}
          data-act="principle"
          onClick={() => s.machine !== null && onPrinciple(s.machine)}
        >
          {t("teacher.room.principle")}
        </Button>
        <Button
          variant="ghost"
          className="pf-btn--sm pf-help__close"
          onClick={onClose}
          data-testid="rc-help-close"
        >
          {t("teacher.room.helpClose")}
        </Button>
      </div>
      {other && (
        <p className="pf-muted pf-rc__why" data-testid="rc-not-mine">
          {t("teacher.room.exchange.notMine", { label: courseLabel })}
        </p>
      )}
    </div>
  );
}
